import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Activity, Coins, Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { TopNav } from "@/components/TopNav";

const fmtTs = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function AdminUsage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (user && !user.is_owner) { navigate("/dashboard"); return; }
    api.get("/usage/logs?limit=100").then(({ data }) => setData(data)).catch(() => setErr(true));
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-background text-white">
      <TopNav />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-white/50 hover:text-white text-sm mb-6" data-testid="admin-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5 text-[#FFD54A]" />
          <h1 className="font-display text-3xl font-semibold tracking-tight">Usage Dashboard</h1>
        </div>
        <p className="text-white/50 text-sm mb-8">Internal activity log — every render & AI action, with credit cost.</p>

        {err ? (
          <div className="text-white/50 text-sm" data-testid="admin-error">Owner access only.</div>
        ) : !data ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" /></div>
        ) : (
          <>
            <div className="grid sm:grid-cols-3 gap-4 mb-8" data-testid="usage-summary">
              <Stat icon={<Activity className="w-4 h-4 text-[#00E5FF]" />} label="Total actions" value={data.total} />
              <Stat icon={<Coins className="w-4 h-4 text-[#39FF14]" />} label="Credits spent (all)" value={data.by_action.reduce((s, a) => s + (a.credits || 0), 0)} />
              <Stat icon={<Activity className="w-4 h-4 text-[#FF007F]" />} label="Action types" value={data.by_action.length} />
            </div>

            <div className="rounded-2xl border border-white/10 bg-zinc-950/60 overflow-hidden" data-testid="usage-table">
              <div className="grid grid-cols-[1fr_1.4fr_1fr_auto] gap-2 px-4 py-2.5 text-[11px] uppercase tracking-wide text-white/40 border-b border-white/10">
                <span>When</span><span>User</span><span>Action</span><span className="text-right">Credits</span>
              </div>
              {data.logs.length === 0 && <div className="px-4 py-8 text-center text-white/40 text-sm">No activity yet.</div>}
              {data.logs.map((l, i) => (
                <div key={i} className="grid grid-cols-[1fr_1.4fr_1fr_auto] gap-2 px-4 py-2.5 text-sm border-b border-white/5 last:border-0" data-testid={`usage-row-${i}`}>
                  <span className="text-white/50">{fmtTs(l.ts)}</span>
                  <span className="text-white/80 truncate">{l.user_email || l.user_name || "—"}{l.owner && <span className="ml-1 text-[10px] text-[#FFD54A]">owner</span>}</span>
                  <span className="text-white/70 capitalize">{l.action}</span>
                  <span className={`text-right font-medium ${l.credits ? "text-white" : "text-[#39FF14]"}`}>{l.credits ? `-${l.credits}` : "0"}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const Stat = ({ icon, label, value }) => (
  <div className="rounded-xl border border-white/10 bg-zinc-950/60 p-4">
    <div className="flex items-center gap-1.5 text-white/40 text-xs uppercase tracking-wide">{icon}{label}</div>
    <div className="font-display text-2xl font-semibold mt-1">{value}</div>
  </div>
);
